import { useState, useRef, useEffect } from 'react';
import { Camera, Trash2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PhoneNumberDisplay from '@/components/ui/PhoneNumberDisplay';
import {
  useFetchUser,
  useUpdateUser,
  useUploadAvatar,
  useFetchCompany,
  useUpdateCompany,
  useUploadCompanyLogo,
  useFetchIndustries,
} from '@/hooksApi';
import { parsePhoneNumber } from '@/utils/helpers/phoneNumberHelpers';
import { useQueryClient } from '@tanstack/react-query';
import useRole from '@/hooks/useRole';
import { RuleName } from '@/types';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const SettingsProfile = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { hasRole } = useRole();
  const isOwner = hasRole(RuleName.OWNER);

  /* ── Personal Info ── */
  const { data: user } = useFetchUser();
  const { mutate: updateUser, isLoading: isUpdatingUser } = useUpdateUser();
  const { mutate: uploadAvatar, isLoading: isUploadingAvatar } = useUploadAvatar();

  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [phoneCode, setPhoneCode] = useState('+964');
  const [nationalPhone, setNationalPhone] = useState('');
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileImgError, setProfileImgError] = useState(false);
  const [profileImgLoaded, setProfileImgLoaded] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (user?.object) {
      if (firstName === null) setFirstName(user.object.first_name ?? '');
      if (lastName === null) setLastName(user.object.last_name ?? '');
      if (user.object.phone) {
        const { phone, countryCode } = parsePhoneNumber(user.object.phone);
        setPhoneCode(`+${countryCode}`);
        setNationalPhone(phone ?? '');
      }
    }
  }, [user]);

  /* ── Company Info ── */
  const { data: company } = useFetchCompany();
  const { mutate: updateCompany, isLoading: isUpdatingCompany } = useUpdateCompany();
  const { mutate: uploadLogo, isLoading: isUploadingLogo } = useUploadCompanyLogo();

  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyIndustry, setCompanyIndustry] = useState<string | null>(null);
  const [companyImage, setCompanyImage] = useState<string | null>(null);
  const [companyImgError, setCompanyImgError] = useState(false);
  const [companyImgLoaded, setCompanyImgLoaded] = useState(false);

  useEffect(() => {
    if (company?.object) {
      if (companyName === null) setCompanyName(company.object.name ?? '');
      if (companyIndustry === null) setCompanyIndustry(company.object.industry ?? '');
    }
  }, [company]);

  /* ── Derived display values ── */
  const userPhone = nationalPhone;
  const displayFirstName = firstName ?? '';
  const displayLastName = lastName ?? '';
  const displayCompanyName = companyName ?? '';
  const displayCompanyIndustry = companyIndustry ?? '';
  const profileAvatarUrl = profileImage || user?.object?.avatar?.abs_url || null;
  const companyLogoUrl = companyImage || company?.object?.avatar?.abs_url || null;

  /* ── Image upload handler ── */
  const profileInputRef = useRef<HTMLInputElement>(null);
  const companyInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (val: string | null) => void,
    onUpload: (formData: FormData) => void,
    invalidateKey: string,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({ title: t('error_title'), description: t('image_format_error'), variant: 'destructive' });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast({ title: t('error_title'), description: t('image_size_error'), variant: 'destructive' });
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      if (img.width < 200 || img.height < 200) {
        toast({ title: t('error_title'), description: t('image_min_size_error'), variant: 'destructive' });
        URL.revokeObjectURL(objectUrl);
        return;
      }
      setter(objectUrl);
      const formData = new FormData();
      formData.append('file', file);
      onUpload(formData);
    };
    img.src = objectUrl;
    e.target.value = '';
  };

  /* ── Validate & submit all ── */
  const handleSave = () => {
    const first = displayFirstName.trim();
    const last = displayLastName.trim();
    const errors: Record<string, boolean> = {};

    if (!first) errors.first_name = true;
    if (!last) errors.last_name = true;

    if (isOwner) {
      if (!displayCompanyName.trim()) errors.company_name = true;
      if (!displayCompanyIndustry.trim()) errors.industry = true;
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const missing = Object.keys(errors).map(k => String(t(k))).join(', ');
      toast({ title: t('required_fields_missing'), description: missing, variant: 'destructive' });
      return;
    }

    // All validations passed — submit
    updateUser(
      { first_name: first, last_name: last },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries(['user']);
          setFirstName(data?.object?.first_name ?? '');
          setLastName(data?.object?.last_name ?? '');

          if (isOwner) {
            updateCompany(
              { name: displayCompanyName.trim(), industry: displayCompanyIndustry.trim() },
              {
                onSuccess: (compData) => {
                  toast({ title: t('success_title'), description: t('success-update-profile') });
                  queryClient.invalidateQueries(['company']);
                  setCompanyName(compData?.object?.name ?? '');
                  setCompanyIndustry(compData?.object?.industry ?? '');
                },
                onError: (error: any) => {
                  const msg = error?.response?.data?.message || error?.message || t('failed-update-profile');
                  toast({ title: t('error_title'), description: msg, variant: 'destructive' });
                },
              },
            );
          } else {
            toast({ title: t('success_title'), description: t('success-update-profile') });
          }
        },
        onError: (error: any) => {
          const msg = error?.response?.data?.message || error?.message || t('failed-update-profile');
          toast({ title: t('error_title'), description: msg, variant: 'destructive' });
        },
      },
    );
  };

  const { data: industriesData } = useFetchIndustries();
  const industries = (industriesData?.object ?? []).map((slug) => ({
    value: slug,
    label: String(t(slug.replace(/-/g, '_'), { defaultValue: slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) })),
  }));

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        {/* Breadcrumb */}
        <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground'>
          <span>{t('settings')}</span>
          <ChevronRight className='h-3.5 w-3.5' strokeWidth={1.5} />
          <span className='text-sd-foreground font-medium'>{t('profile')}</span>
        </nav>

        <h1 className='text-xl font-semibold text-sd-foreground m-0'>{t('profile')}</h1>

        {/* ── Personal Information ── */}
        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          <div className='px-6 py-4 border-b border-sd-border'>
            <h2 className='text-sm font-semibold text-sd-foreground m-0'>
              {t('personal_information')}
            </h2>
          </div>
          <div className='p-6 space-y-6'>
            {/* Avatar */}
            <div className='flex items-start gap-5'>
              <div className='relative'>
                <div className='h-20 w-20 rounded-full bg-sd-muted flex items-center justify-center overflow-hidden border-2 border-sd-border relative'>
                  <span className='text-xl font-semibold text-sd-muted-foreground'>
                    {displayFirstName[0] ?? '?'}
                    {displayLastName[0] ?? ''}
                  </span>
                  {profileAvatarUrl && (
                    <img
                      src={profileAvatarUrl}
                      alt='Profile'
                      className={`absolute inset-0 h-full w-full object-cover${!profileImgLoaded || profileImgError ? ' hidden' : ''}`}
                      onLoad={() => setProfileImgLoaded(true)}
                      onError={() => setProfileImgError(true)}
                    />
                  )}
                </div>
                <button
                  type='button'
                  onClick={() => profileInputRef.current?.click()}
                  disabled={isUploadingAvatar}
                  className='absolute -bottom-1 ltr:-right-1 rtl:-left-1 h-8 w-8 rounded-full bg-sd-primary flex items-center justify-center border-2 border-sd-card shadow-sm hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-60'
                >
                  <Camera className='h-3.5 w-3.5 text-sd-primary-foreground' />
                </button>
                <input
                  ref={profileInputRef}
                  type='file'
                  accept='.jpg,.jpeg,.png,.webp'
                  className='hidden'
                  onChange={(e) =>
                    handleImageUpload(
                      e,
                      setProfileImage,
                      (fd) =>
                        uploadAvatar(fd, {
                          onSuccess: () => {
                            toast({ title: t('success_title'), description: t('success-update-avatar') });
                            queryClient.invalidateQueries(['user']);
                          },
                          onError: (error: any) => {
                            const msg = error?.response?.data?.message || error?.message || t('failed-update-avatar');
                            toast({ title: t('error_title'), description: msg, variant: 'destructive' });
                          },
                        }),
                      'user',
                    )
                  }
                />
              </div>
              <div className='flex flex-col gap-1.5 pt-1'>
                <p className='text-xs text-sd-muted-foreground'>{t('image_upload_hint')}</p>
                {profileImage && (
                  <button
                    type='button'
                    onClick={() => setProfileImage(null)}
                    className='flex items-center gap-1.5 text-xs font-medium text-sd-destructive hover:underline w-fit'
                  >
                    <Trash2 className='h-3 w-3' />
                    {t('remove_photo')}
                  </button>
                )}
              </div>
            </div>

            {/* Name fields */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <div className='space-y-1.5'>
                <label className='text-xs font-medium text-sd-muted-foreground'>
                  {t('first_name')}
                </label>
                <input
                  type='text'
                  value={displayFirstName}
                  onChange={(e) => { setFirstName(e.target.value); setFieldErrors(prev => ({ ...prev, first_name: false })); }}
                  className={`w-full h-10 px-3 rounded-lg border text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring ${fieldErrors.first_name ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'}`}
                />
                {fieldErrors.first_name && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
              </div>
              <div className='space-y-1.5'>
                <label className='text-xs font-medium text-sd-muted-foreground'>
                  {t('last_name')}
                </label>
                <input
                  type='text'
                  value={displayLastName}
                  onChange={(e) => { setLastName(e.target.value); setFieldErrors(prev => ({ ...prev, last_name: false })); }}
                  className={`w-full h-10 px-3 rounded-lg border text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring ${fieldErrors.last_name ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'}`}
                />
                {fieldErrors.last_name && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
              </div>
            </div>

            {/* Phone (display only) */}
            <div className='space-y-1.5'>
              <label className='text-xs font-medium text-sd-muted-foreground'>
                {t('phone_number')}
              </label>
              <PhoneNumberDisplay phoneCode={phoneCode} nationalPhone={userPhone} readOnly />
            </div>
          </div>
        </div>

        {/* ── Company Information ── */}
        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          <div className='px-6 py-4 border-b border-sd-border'>
            <h2 className='text-sm font-semibold text-sd-foreground m-0'>
              {t('company_information')}
            </h2>
          </div>
          <div className='p-6 space-y-6'>
            {/* Company Logo */}
            <div className='flex items-start gap-5'>
              <div className='relative'>
                <div className='h-20 w-20 rounded-xl bg-sd-muted flex items-center justify-center overflow-hidden border-2 border-sd-border relative'>
                  <Camera className='h-6 w-6 text-sd-muted-foreground' />
                  {companyLogoUrl && (
                    <img
                      src={companyLogoUrl}
                      alt='Company'
                      className={`absolute inset-0 h-full w-full object-cover${!companyImgLoaded || companyImgError ? ' hidden' : ''}`}
                      onLoad={() => setCompanyImgLoaded(true)}
                      onError={() => setCompanyImgError(true)}
                    />
                  )}
                </div>
                <button
                  type='button'
                  onClick={() => (!isOwner ? undefined : companyInputRef.current?.click())}
                  disabled={!isOwner || isUploadingLogo}
                  className='absolute -bottom-1 ltr:-right-1 rtl:-left-1 h-8 w-8 rounded-full bg-sd-primary flex items-center justify-center border-2 border-sd-card shadow-sm hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed'
                >
                  <Camera className='h-3.5 w-3.5 text-sd-primary-foreground' />
                </button>
                <input
                  ref={companyInputRef}
                  type='file'
                  accept='.jpg,.jpeg,.png,.webp'
                  className='hidden'
                  disabled={!isOwner}
                  onChange={(e) =>
                    handleImageUpload(
                      e,
                      setCompanyImage,
                      (fd) =>
                        uploadLogo(fd, {
                          onSuccess: () => {
                            toast({ title: t('success_title'), description: t('success-update-avatar') });
                            queryClient.invalidateQueries(['company']);
                          },
                          onError: (error: any) => {
                            const msg = error?.response?.data?.message || error?.message || t('failed-update-avatar');
                            toast({ title: t('error_title'), description: msg, variant: 'destructive' });
                          },
                        }),
                      'company',
                    )
                  }
                />
              </div>
              <div className='flex flex-col gap-1.5 pt-1'>
                <p className='text-xs text-sd-muted-foreground'>{t('image_upload_hint')}</p>
                {companyImage && isOwner && (
                  <button
                    type='button'
                    onClick={() => setCompanyImage(null)}
                    className='flex items-center gap-1.5 text-xs font-medium text-sd-destructive hover:underline w-fit'
                  >
                    <Trash2 className='h-3 w-3' />
                    {t('remove_logo')}
                  </button>
                )}
              </div>
            </div>

            {/* Company Name */}
            <div className='space-y-1.5'>
              <label className='text-xs font-medium text-sd-muted-foreground'>
                {t('company_name')}
              </label>
              <input
                type='text'
                value={displayCompanyName}
                onChange={(e) => { setCompanyName(e.target.value); setFieldErrors(prev => ({ ...prev, company_name: false })); }}
                disabled={!isOwner}
                placeholder={t('company_name')}
                className={`w-full h-10 px-3 rounded-lg border text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring disabled:opacity-50 disabled:cursor-not-allowed ${fieldErrors.company_name ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'}`}
              />
              {fieldErrors.company_name && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
            </div>

            {/* Company Industry */}
            <div className='space-y-1.5'>
              <label className='text-xs font-medium text-sd-muted-foreground'>
                {t('industry')}
              </label>
              <Select
                value={displayCompanyIndustry}
                onValueChange={(v) => { setCompanyIndustry(v); setFieldErrors(prev => ({ ...prev, industry: false })); }}
                disabled={!isOwner}
              >
                <SelectTrigger className={`w-full h-10 rounded-lg border text-sm focus:ring-2 focus:ring-sd-ring ${fieldErrors.industry ? 'border-red-500 bg-red-50' : 'border-sd-input'}`}>
                  <SelectValue placeholder={t('industry')} />
                </SelectTrigger>
                <SelectContent className='bg-sd-popover'>
                  {industries.map((ind) => (
                    <SelectItem key={ind.value} value={ind.value}>
                      {ind.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.industry && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
            </div>
          </div>
        </div>

        {/* Save */}
        <div className='flex justify-end'>
          <button
            type='button'
            onClick={handleSave}
            disabled={isUpdatingUser || isUploadingAvatar || isUpdatingCompany || isUploadingLogo}
            className='px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {isUpdatingUser || isUpdatingCompany ? t('loading') : t('save_changes')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsProfile;
